import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useFriends } from '../features/social/hooks/useFriends';
import { useFriendRequests } from '../features/social/hooks/useFriendRequests';
import { useFriendshipLinks } from '../features/social/hooks/useFriendshipLinks';
import { useUserSearch } from '../features/social/hooks/useUserSearch';
import { useChat } from '../features/social/hooks/useChat';
import { useAuth } from '../features/auth/contexts/AuthContext';
import { useWebSocket } from '../features/social/hooks/useWebSocket';
import { MessageNotification } from '../services/websocketService';
import { Tooltip } from '../shared/components/UI';

const FriendsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const FriendsCard = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  overflow: visible;
`;

const FriendsHeader = styled.div`
  padding: 2rem;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  text-align: center;
`;

const FriendsTitle = styled.h1`
  font-family: var(--font-serif);
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-gray-900);
  margin-bottom: 0.5rem;
`;

const FriendsSubtitle = styled.p`
  color: var(--color-gray-600);
  font-size: 1.1rem;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--color-gray-200);
  background: var(--color-gray-50);
`;

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 1rem;
  border: none;
  background: ${props => props.active ? 'var(--color-white)' : 'transparent'};
  color: ${props => props.active ? 'var(--color-gray-900)' : 'var(--color-gray-600)'};
  font-weight: ${props => props.active ? '600' : '500'};
  font-family: var(--font-sans);
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? 'var(--color-primary)' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-white);
    color: var(--color-gray-900);
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.75rem 0.5rem;
  }
`;

const TabContent = styled.div`
  padding: 2rem;
  min-height: 400px;
`;

const SearchSection = styled.div`
  margin-bottom: 2rem;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-family: var(--font-sans);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const SearchButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--color-primary);
  color: var(--color-gray-800);
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #8BB5E0;
  }
`;

const FriendshipLinkSection = styled.div`
  background: var(--color-gray-50);
  padding: 1.5rem;
  border-radius: var(--border-radius-md);
  margin-bottom: 1rem;
`;

const LinkTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin-bottom: 1rem;
`;

const LinkDescription = styled.p`
  color: var(--color-gray-600);
  margin-bottom: 1rem;
`;

const LinkControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const GeneratedLink = styled.div`
  flex: 1;
  padding: 0.75rem;
  background: var(--color-white);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-family: monospace;
  font-size: 0.9rem;
  color: var(--color-gray-700);
  word-break: break-all;
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: visible;
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: var(--color-gray-50);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-gray-200);
  transition: border-color 0.2s ease;
  overflow: visible;

  &:hover {
    border-color: var(--color-primary);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const UserAvatar = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-gray-800);
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const UserName = styled.h4`
  font-family: var(--font-sans);
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin: 0;
`;

const UserUsername = styled.span`
  color: var(--color-gray-500);
  font-size: 0.85rem;
`;

const UserBio = styled.p`
  color: var(--color-gray-600);
  font-size: 0.9rem;
  margin: 0;
  max-width: 300px;
`;

const UserActions = styled.div`
  display: flex;
  gap: 0.75rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: var(--color-red-600);
          color: white;
          &:hover { background: var(--color-red-700); }
        `;
      case 'secondary':
        return `
          background: var(--color-secondary);
          color: var(--color-gray-800);
          &:hover { background: #FBF0B3; }
        `;
      default:
        return `
          background: var(--color-primary);
          color: var(--color-gray-800);
          &:hover { background: var(--color-primary-dark); }
        `;
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--color-gray-500);
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-gray-600);
  margin-bottom: 0.5rem;
`;

const EmptyStateText = styled.p`
  color: var(--color-gray-500);
`;

// Chat Modal Styles
const ChatModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ChatWindow = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  height: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ChatHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--color-gray-200);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChatTitle = styled.h3`
  font-family: var(--font-sans);
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin: 0;
`;

const ChatClose = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--color-gray-500);
  cursor: pointer;
  
  &:hover {
    color: var(--color-gray-700);
  }
`;

const ChatMessages = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ChatMessage = styled.div<{ isOwn?: boolean }>`
  padding: 0.75rem 1rem;
  border-radius: 18px;
  max-width: 70%;
  word-wrap: break-word;
  align-self: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
  background: ${props => props.isOwn ? 'var(--color-primary)' : 'var(--color-gray-100)'};
  color: ${props => props.isOwn ? 'white' : 'var(--color-gray-900)'};
  margin-bottom: 0.5rem;
`;

const MessageTime = styled.div`
  font-size: 0.75rem;
  color: var(--color-gray-500);
  margin-top: 0.25rem;
  text-align: right;
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: var(--color-gray-500);
  padding: 1rem;
  font-style: italic;
`;

const ChatInputArea = styled.div`
  padding: 1rem;
  border-top: 1px solid var(--color-gray-200);
  display: flex;
  gap: 0.75rem;
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid var(--color-gray-300);
  border-radius: 24px;
  font-size: 0.95rem;
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const ChatSendButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: 24px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: var(--color-primary-dark);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ChatPlaceholder = styled.div`
  text-align: center;
  color: var(--color-gray-500);
  padding: 2rem;
  font-style: italic;
`;

// Confirmation Modal Styles
const ConfirmModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 1rem;
`;

const ConfirmContent = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  max-width: 450px;
  width: 100%;
`;

const ConfirmHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
`;

const ConfirmTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
  font-family: var(--font-sans);
`;

const ConfirmBody = styled.div`
  padding: 1.5rem;
`;

const ConfirmText = styled.p`
  color: var(--color-gray-700);
  margin: 0;
  line-height: 1.6;
`;

const ConfirmFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid var(--color-gray-200);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const ConfirmButton = styled.button<{ $variant?: 'danger' | 'secondary' }>`
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    if (props.$variant === 'danger') {
      return `
        background: var(--color-red-600);
        border-color: var(--color-red-600);
        color: white;
        &:hover {
          background: var(--color-red-700);
        }
      `;
    }
    return `
      background: white;
      border-color: var(--color-gray-300);
      color: var(--color-gray-700);
      &:hover {
        background: var(--color-gray-50);
      }
    `;
  }}
`;

const Friends: React.FC = () => {
  console.log('🎯 Friends component is rendering');
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('friends');
  const [searchTerm, setSearchTerm] = useState('');
  const [friendshipLink, setFriendshipLink] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState<{id: string, name: string} | null>(null);
  const [chatMessage, setChatMessage] = useState('');
  const [pendingFriendRequests, setPendingFriendRequests] = useState<Set<string>>(new Set());

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [friendToRemove, setFriendToRemove] = useState<{id: string, name: string} | null>(null);

  // Refs for chat functionality
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  
  // Authentication and user data
  const { user } = useAuth();
  
  // Real API data using custom hooks
  const { friends, loadFriends, removeFriend } = useFriends();
  const { 
    pendingRequests, 
    sentRequests, 
    loadAllRequests,
    acceptFriendRequest,
    rejectFriendRequest,
    sendFriendRequest
  } = useFriendRequests();
  const {
    loadAllLinks,
    createFriendshipLink
  } = useFriendshipLinks();
  
  const {
    searchResults,
    hasSearched,
    isLoading: isSearching,
    error: searchError,
    searchUsers,
    clearSearch
  } = useUserSearch();

  const {
    conversations,
    messages,
    isLoading: isLoadingChat,
    isSending,
    sendMessage,
    loadConversations,
    loadMessages,
    markMessagesAsRead,
    findOrCreateConversation,
    addMessageToConversation
  } = useChat();

  // Handle real-time message notifications
  const handleMessageNotification = (notification: MessageNotification) => {
    // Add the message to the conversation
    const message = {
      id: notification.id,
      conversationId: notification.conversationId,
      senderId: notification.senderId,
      content: notification.content,
      isRead: false,
      readAt: undefined,
      createdAt: notification.createdAt,
      updatedAt: notification.createdAt
    };
    
    addMessageToConversation(notification.conversationId, message);
    
    // Refresh conversations to update last message and unread count
    loadConversations();
  };

  // WebSocket connection for real-time messages
  console.log('🏠 Friends component: About to call useWebSocket hook');
  useWebSocket(handleMessageNotification);
  console.log('🏠 Friends component: useWebSocket hook called');

  // Scroll to bottom of chat
  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  // Auto-scroll when messages change or chat opens
  useEffect(() => {
    if (showChat && selectedFriend) {
      const conversation = findOrCreateConversation(selectedFriend.id, selectedFriend.name);
      const conversationMessages = conversation.id ? messages[conversation.id] : [];
      if (conversationMessages && conversationMessages.length > 0) {
        setTimeout(scrollToBottom, 100); // Small delay to ensure DOM is updated
      }
    }
  }, [showChat, selectedFriend, messages, findOrCreateConversation]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        loadFriends(),
        loadAllRequests(),
        loadAllLinks(),
        loadConversations()
      ]);
    };
    loadData();
  }, [loadFriends, loadAllRequests, loadAllLinks, loadConversations]);

  // Function to update pending friend requests set
  const updatePendingRequests = useCallback(() => {
    const pending = new Set<string>();
    
    // Add sent requests (users we've sent requests to)
    sentRequests.forEach(request => {
      if (request.addresseeId) {
        pending.add(request.addresseeId);
      }
    });
    
    // Add existing friends (users we're already friends with)
    friends.forEach(friend => {
      pending.add(friend.userId);
    });
    
    // Add incoming pending requests (users who sent us requests)
    pendingRequests.forEach(request => {
      if (request.requesterId) {
        pending.add(request.requesterId);
      }
    });
    
    setPendingFriendRequests(pending);
  }, [friends, sentRequests, pendingRequests]);

  // Update pending requests when data changes
  useEffect(() => {
    updatePendingRequests();
  }, [friends, sentRequests, pendingRequests, updatePendingRequests]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      await searchUsers(searchTerm.trim());
    } else {
      clearSearch();
    }
  };

  const handleGenerateLink = async () => {
    try {
      const link = await createFriendshipLink({ expiresInHours: 24, maxUses: 10 });
      if (link) {
        const fullLink = `${window.location.origin}/friends/invite/${link.token}`;
        setFriendshipLink(fullLink);
      }
    } catch (error) {
      console.error('Failed to create friendship link:', error);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(friendshipLink);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleSendFriendRequest = async (userId: string, userName: string) => {
    try {
      // Optimistically add to pending set to disable button immediately
      setPendingFriendRequests(prev => {
        const newSet = new Set(prev);
        newSet.add(userId);
        return newSet;
      });
      
      await sendFriendRequest({ addresseeId: userId });
      toast.success(`Friend request sent to ${userName}! 👫`);
      
      // Refresh the requests list to get the latest data
      await loadAllRequests();
      
      // Refresh search results to update button state
      if (searchTerm.trim()) {
        await searchUsers(searchTerm.trim());
      }
    } catch (error: any) {
      console.error('Failed to send friend request:', error);
      
      // Remove from pending set if request failed
      setPendingFriendRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      
      // Parse error message for user-friendly display
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      
      if (errorMessage.includes('already pending')) {
        toast.error(`Friend request to ${userName} is already pending ⏳`);
      } else if (errorMessage.includes('Already friends')) {
        toast.error(`You're already friends with ${userName}! 👥`);
      } else if (errorMessage.includes('Cannot send friend request')) {
        toast.error(`Cannot send friend request to ${userName} 🚫`);
      } else if (errorMessage.includes('Friendship already exists')) {
        toast.error(`A friendship connection already exists with ${userName} 👥`);
      } else {
        toast.error(`Failed to send friend request to ${userName}: ${errorMessage}`);
      }
    }
  };

  const handleAcceptRequest = async (requestId: string, userName: string) => {
    try {
      await acceptFriendRequest(requestId);
      await loadFriends(); // Refresh friends list
      await loadAllRequests(); // Refresh requests lists
      toast.success(`You're now friends with ${userName}! 🎉`);
    } catch (error: any) {
      console.error('Failed to accept friend request:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      toast.error(`Failed to accept friend request from ${userName}: ${errorMessage}`);
    }
  };

  const handleRejectRequest = async (requestId: string, userName: string) => {
    try {
      await rejectFriendRequest(requestId);
      await loadAllRequests(); // Refresh requests lists to remove the request
      toast.success(`Friend request from ${userName} was rejected. They can send a new request later if needed. ✋`);
    } catch (error: any) {
      console.error('Failed to reject friend request:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      toast.error(`Failed to reject friend request from ${userName}: ${errorMessage}`);
    }
  };

  const handleViewStories = (friendId: string, friendName: string) => {
    const searchParams = new URLSearchParams();
    searchParams.set('tab', 'friends');
    searchParams.set('friend', friendName);
    window.location.href = `/dashboard?${searchParams.toString()}`;
  };

  const handleMessage = async (friendId: string, friendName: string) => {
    setSelectedFriend({ id: friendId, name: friendName });
    setShowChat(true);
    
    // Load conversation messages
    const conversation = findOrCreateConversation(friendId, friendName);
    if (conversation.id) {
      await loadMessages(conversation.id);
      // Mark messages as read when opening the conversation
      await markMessagesAsRead(conversation.id);
    }
  };

  const handleCloseChat = () => {
    setShowChat(false);
    setSelectedFriend(null);
    setChatMessage('');
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (chatMessage.trim() && selectedFriend) {
      try {
        await sendMessage(selectedFriend.id, chatMessage);
        setChatMessage('');
        // Scroll to bottom after sending
        setTimeout(scrollToBottom, 100);
      } catch (error: any) {
        console.error('Failed to send message:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
        toast.error(`Failed to send message: ${errorMessage}`);
      }
    }
  };

  const openRemoveFriendModal = (friendId: string, friendName: string) => {
    setFriendToRemove({ id: friendId, name: friendName });
    setShowConfirmModal(true);
  };

  const closeRemoveFriendModal = () => {
    setShowConfirmModal(false);
    setFriendToRemove(null);
  };

  const handleRemoveFriend = async () => {
    if (!friendToRemove) return;

    // Find the friendship to remove
    const friend = friends.find(f => f.userId === friendToRemove.id);
    if (friend) {
      try {
        await removeFriend(friend.friendshipId);
        toast.success(`${friendToRemove.name} has been removed from your friends list 👋`);
        closeRemoveFriendModal();
      } catch (error: any) {
        console.error('Failed to remove friend:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
        toast.error(`Failed to remove ${friendToRemove.name}: ${errorMessage}`);
      }
    }
  };

  const getUserInitials = (fullName: string, username: string) => {
    if (fullName) {
      return fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return username.slice(0, 2).toUpperCase();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'search':
        return (
          <div>
            <SearchSection>
              <SearchForm onSubmit={handleSearch}>
                <SearchInput
                  type="text"
                  placeholder={t('friends.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <SearchButton type="submit">{t('friends.search')}</SearchButton>
              </SearchForm>

              <FriendshipLinkSection>
                <LinkTitle>Share Friendship Link</LinkTitle>
                <LinkDescription>
                  Generate a link to share with friends so they can easily send you a friend request, 
                  even if they don't have a BookKids account yet.
                </LinkDescription>
                <LinkControls>
                  {friendshipLink ? (
                    <>
                      <GeneratedLink>{friendshipLink}</GeneratedLink>
                      <ActionButton onClick={handleCopyLink}>Copy Link</ActionButton>
                    </>
                  ) : (
                    <ActionButton onClick={handleGenerateLink}>Generate Link</ActionButton>
                  )}
                </LinkControls>
              </FriendshipLinkSection>
            </SearchSection>

            {isSearching ? (
              <EmptyState>
                <EmptyStateIcon>⏳</EmptyStateIcon>
                <EmptyStateTitle>{t('friends.searching')}</EmptyStateTitle>
                <EmptyStateText>Finding users matching your search.</EmptyStateText>
              </EmptyState>
            ) : searchError ? (
              <EmptyState>
                <EmptyStateIcon>❌</EmptyStateIcon>
                <EmptyStateTitle>{t('friends.searchError')}</EmptyStateTitle>
                <EmptyStateText>Failed to search users. Please try again.</EmptyStateText>
              </EmptyState>
            ) : searchResults.length > 0 ? (
              <UserList>
                {searchResults.map((user) => (
                  <UserCard key={user.id}>
                    <UserInfo>
                      <UserAvatar>
                        {getUserInitials(user.fullName || `${user.firstName} ${user.lastName}`, user.username)}
                      </UserAvatar>
                      <UserDetails>
                        <UserName>{user.fullName || `${user.firstName} ${user.lastName}` || user.username}</UserName>
                        <UserUsername>@{user.username}</UserUsername>
                        {user.bio && <UserBio>{user.bio}</UserBio>}
                      </UserDetails>
                    </UserInfo>
                    <UserActions>
                      <ActionButton 
                        onClick={() => handleSendFriendRequest(user.id, user.fullName || user.username)}
                        disabled={pendingFriendRequests.has(user.id)}
                      >
                        {pendingFriendRequests.has(user.id) ? 'Request Sent ✓' : t('friends.addFriend')}
                      </ActionButton>
                    </UserActions>
                  </UserCard>
                ))}
              </UserList>
            ) : hasSearched ? (
              <EmptyState>
                <EmptyStateIcon>🔍</EmptyStateIcon>
                <EmptyStateTitle>No Users Found</EmptyStateTitle>
                <EmptyStateText>Try searching with different keywords.</EmptyStateText>
              </EmptyState>
            ) : null}
          </div>
        );

      case 'requests':
        return (
          <div>
            {/* Incoming Requests */}
            {pendingRequests.length > 0 && (
              <>
                <div style={{ padding: '1rem', borderBottom: '1px solid var(--color-gray-200)', background: 'var(--color-gray-50)' }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: 'var(--color-gray-800)' }}>
                    Received Requests ({pendingRequests.length})
                  </h3>
                </div>
                <UserList>
                  {pendingRequests.map((request) => (
                    <UserCard key={request.id}>
                      <UserInfo>
                        <UserAvatar>
                          {getUserInitials(request.requesterName || 'Unknown User', request.requesterUsername || 'unknown')}
                        </UserAvatar>
                        <UserDetails>
                          <UserName>{request.requesterName || 'Friend Request'}</UserName>
                          <UserUsername>@{request.requesterUsername || 'unknown'}</UserUsername>
                          <UserBio>Received: {new Date(request.createdAt).toLocaleDateString()}</UserBio>
                        </UserDetails>
                      </UserInfo>
                      <UserActions>
                        <ActionButton onClick={() => handleAcceptRequest(request.id, request.requesterName || 'Unknown User')}>
                          {t('friends.accept')}
                        </ActionButton>
                        <ActionButton 
                          variant="secondary" 
                          onClick={() => handleRejectRequest(request.id, request.requesterName || 'Unknown User')}
                        >
                          {t('friends.decline')}
                        </ActionButton>
                      </UserActions>
                    </UserCard>
                  ))}
                </UserList>
              </>
            )}

            {/* Sent Requests */}
            {sentRequests.length > 0 && (
              <>
                <div style={{ padding: '1rem', borderBottom: '1px solid var(--color-gray-200)', background: 'var(--color-gray-50)' }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: 'var(--color-gray-800)' }}>
                    Sent Requests ({sentRequests.length})
                  </h3>
                </div>
                <UserList>
                  {sentRequests.map((request) => (
                    <UserCard key={request.id}>
                      <UserInfo>
                        <UserAvatar>
                          {getUserInitials(request.addresseeName || 'Unknown User', request.addresseeUsername || 'unknown')}
                        </UserAvatar>
                        <UserDetails>
                          <UserName>{request.addresseeName || 'Friend Request Sent'}</UserName>
                          <UserUsername>@{request.addresseeUsername || 'unknown'}</UserUsername>
                          <UserBio>Sent: {new Date(request.createdAt).toLocaleDateString()}</UserBio>
                        </UserDetails>
                      </UserInfo>
                      <UserActions>
                        <ActionButton 
                          variant="secondary"
                          onClick={() => handleRejectRequest(request.id, request.addresseeName || 'Unknown User')}
                        >
                          Cancel Request
                        </ActionButton>
                      </UserActions>
                    </UserCard>
                  ))}
                </UserList>
              </>
            )}

            {/* Empty State */}
            {pendingRequests.length === 0 && sentRequests.length === 0 && (
              <EmptyState>
                <EmptyStateIcon>📬</EmptyStateIcon>
                <EmptyStateTitle>{t('friends.noRequests')}</EmptyStateTitle>
                <EmptyStateText>You don't have any pending friend requests.</EmptyStateText>
              </EmptyState>
            )}
          </div>
        );

      case 'friends':
      default:
        return (
          <div>
            {friends.length > 0 ? (
              <UserList>
                {friends.map((friend) => (
                  <UserCard key={friend.userId}>
                    <UserInfo>
                      <UserAvatar>
                        {getUserInitials(friend.fullName, friend.username)}
                      </UserAvatar>
                      <UserDetails>
                        <UserName>{friend.fullName || friend.username}</UserName>
                        <UserUsername>@{friend.username}</UserUsername>
                        {friend.bio && <UserBio>{friend.bio}</UserBio>}
                      </UserDetails>
                    </UserInfo>
                    <UserActions>
                      <Tooltip content="View all stories by this friend" position="top">
                        <ActionButton onClick={() => handleViewStories(friend.userId, friend.fullName || friend.username)}>
                          {t('friends.viewStories')}
                        </ActionButton>
                      </Tooltip>
                      <Tooltip content="Send a direct message" position="top">
                        <ActionButton variant="secondary" onClick={() => handleMessage(friend.userId, friend.fullName || friend.username)}>
                          {t('friends.message')}
                        </ActionButton>
                      </Tooltip>
                      <Tooltip content="Remove this friend from your list" position="top">
                        <ActionButton
                          variant="danger"
                          onClick={() => openRemoveFriendModal(friend.userId, friend.fullName || friend.username)}
                        >
                          {t('friends.remove')}
                        </ActionButton>
                      </Tooltip>
                    </UserActions>
                  </UserCard>
                ))}
              </UserList>
            ) : (
              <EmptyState>
                <EmptyStateIcon>👥</EmptyStateIcon>
                <EmptyStateTitle>No Friends Yet</EmptyStateTitle>
                <EmptyStateText>
                  Start building your BookKids community by searching for and adding friends!
                </EmptyStateText>
              </EmptyState>
            )}
          </div>
        );
    }
  };

  return (
    <FriendsContainer>
      <FriendsCard>
        <FriendsHeader>
          <FriendsTitle>{t('friends.title')}</FriendsTitle>
          <FriendsSubtitle>{t('friends.subtitle')}</FriendsSubtitle>
        </FriendsHeader>

        <TabsContainer>
          <Tab active={activeTab === 'friends'} onClick={() => setActiveTab('friends')}>
            {t('friends.myFriends')} ({friends.length})
          </Tab>
          <Tab active={activeTab === 'requests'} onClick={() => setActiveTab('requests')}>
            {t('friends.requests')} ({pendingRequests.length + sentRequests.length})
          </Tab>
          <Tab active={activeTab === 'search'} onClick={() => setActiveTab('search')}>
            {t('friends.findFriends')}
          </Tab>
        </TabsContainer>

        <TabContent>
          {renderTabContent()}
        </TabContent>
      </FriendsCard>

      {/* Chat Modal */}
      {showChat && selectedFriend && (
        <ChatModal onClick={handleCloseChat}>
          <ChatWindow onClick={(e) => e.stopPropagation()}>
            <ChatHeader>
              <ChatTitle>{t('friends.chatWith', { name: selectedFriend.name })}</ChatTitle>
              <ChatClose onClick={handleCloseChat}>&times;</ChatClose>
            </ChatHeader>
            
            <ChatMessages ref={chatMessagesRef}>
              {isLoadingChat ? (
                <LoadingMessage>{t('friends.loadingMessages')}</LoadingMessage>
              ) : selectedFriend ? (() => {
                const conversation = findOrCreateConversation(selectedFriend.id, selectedFriend.name);
                const conversationMessages = conversation.id ? messages[conversation.id] || [] : [];
                
                if (conversationMessages.length === 0) {
                  return (
                    <ChatPlaceholder>
                      Start a conversation with {selectedFriend.name}!
                    </ChatPlaceholder>
                  );
                }
                
                return conversationMessages.map((message) => {
                  const isOwn = message.senderId === user?.id;
                  const messageTime = new Date(message.createdAt).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  });
                  
                  return (
                    <div key={message.id}>
                      <ChatMessage isOwn={isOwn}>
                        {message.content}
                      </ChatMessage>
                      <MessageTime style={{ textAlign: isOwn ? 'right' : 'left' }}>
                        {messageTime}
                      </MessageTime>
                    </div>
                  );
                });
              })() : null}
            </ChatMessages>
            
            <ChatInputArea>
              <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
                <ChatInput
                  type="text"
                  placeholder={t('friends.typePlaceholder')}
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                />
                <ChatSendButton type="submit" disabled={!chatMessage.trim() || isSending}>
                  {isSending ? t('friends.sending') : t('friends.send')}
                </ChatSendButton>
              </form>
            </ChatInputArea>
          </ChatWindow>
        </ChatModal>
      )}

      {/* Remove Friend Confirmation Modal */}
      {showConfirmModal && friendToRemove && (
        <ConfirmModal onClick={closeRemoveFriendModal}>
          <ConfirmContent onClick={(e) => e.stopPropagation()}>
            <ConfirmHeader>
              <ConfirmTitle>Remove Friend</ConfirmTitle>
            </ConfirmHeader>
            <ConfirmBody>
              <ConfirmText>
                Are you sure you want to remove <strong>{friendToRemove.name}</strong> from your friends?
                You can send them a friend request again later if you change your mind.
              </ConfirmText>
            </ConfirmBody>
            <ConfirmFooter>
              <ConfirmButton onClick={closeRemoveFriendModal}>
                Cancel
              </ConfirmButton>
              <ConfirmButton $variant="danger" onClick={handleRemoveFriend}>
                Remove Friend
              </ConfirmButton>
            </ConfirmFooter>
          </ConfirmContent>
        </ConfirmModal>
      )}
    </FriendsContainer>
  );
};

export default Friends;